import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import ScriptureCard from '../components/ScriptureCard';
import { 
  BookOpen, 
  Heart, 
  Compass, 
  FlaskConical, 
  ArrowRight, 
  Library, 
  Users,
  Trophy,
  BookMarked,
  CheckCircle,
  ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hubsInfo = [
  { id: 'gospel', title: 'Gospel', icon: BookOpen, path: '/gospel' },
  { id: 'identity', title: 'Identity', icon: Heart, path: '/identity' },
  { id: 'hermeneutics', title: 'Hermeneutics', icon: Compass, path: '/hermeneutics' },
  { id: 'exegesis', title: 'Exegesis', icon: FlaskConical, path: '/exegesis' },
  { id: 'whatnow', title: 'What Now?', icon: ArrowRight, path: '/what-now' },
  { id: 'resources', title: 'Resources', icon: Library, path: '/resources' },
  { id: 'leaders', title: 'For Leaders', icon: Users, path: '/leaders' },
];

export default function DashboardPage() {
  const { user, getAuthHeaders } = useAuth();
  const [progress, setProgress] = useState(null);
  const [dailyScripture, setDailyScripture] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progressRes, scriptureRes] = await Promise.all([
          axios.get(`${API}/progress`, { headers: getAuthHeaders() }),
          axios.get(`${API}/scripture-cards/daily`)
        ]);
        setProgress(progressRes.data);
        setDailyScripture(scriptureRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const completedCount = progress?.completed_quizzes?.length || 0;
  const totalHubs = 7;
  const overallProgress = (completedCount / totalHubs) * 100;

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] py-8 px-4 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Welcome Header */}
        <div className="animate-fade-in">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-2">
            Welcome, {user?.name}
          </h1>
          <p className="text-muted-foreground">
            Continue your discipleship journey and grow in grace.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-slide-up">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Overall Progress
              </CardDescription>
              <CardTitle className="text-3xl font-serif">
                {completedCount}/{totalHubs}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Progress value={overallProgress} className="h-2" />
              <p className="text-sm text-muted-foreground mt-2">
                {totalHubs - completedCount} hubs remaining
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <BookMarked className="h-4 w-4" />
                Journal Entries
              </CardDescription>
              <CardTitle className="text-3xl font-serif">
                {progress?.journals_count || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Reflections saved
              </p>
              <Link 
                to="/identity" 
                className="text-sm text-primary hover:underline inline-flex items-center mt-2"
              >
                Write new entry <ChevronRight className="h-4 w-4" />
              </Link>
            </CardContent>
          </Card>

          <Card className="sm:col-span-2 lg:col-span-1">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Best Score
              </CardDescription>
              <CardTitle className="text-3xl font-serif">
                {progress?.quiz_scores && Object.keys(progress.quiz_scores).length > 0
                  ? `${Math.max(...Object.values(progress.quiz_scores)).toFixed(0)}%`
                  : '—'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Highest quiz score achieved
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Daily Scripture & Hub Progress */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Daily Scripture */}
          {dailyScripture && (
            <div className="animate-slide-up delay-100">
              <h2 className="font-serif text-xl font-semibold mb-4">Today's Scripture</h2>
              <ScriptureCard card={dailyScripture} />
            </div>
          )}

          {/* Hub Progress */}
          <div className="animate-slide-up delay-200">
            <h2 className="font-serif text-xl font-semibold mb-4">Hub Progress</h2>
            <Card>
              <CardContent className="p-4 space-y-3">
                {hubsInfo.map((hub) => {
                  const Icon = hub.icon;
                  const isCompleted = progress?.completed_quizzes?.includes(hub.id);
                  const score = progress?.quiz_scores?.[hub.id];
                  
                  return (
                    <Link 
                      key={hub.id} 
                      to={hub.path}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors group"
                      data-testid={`dashboard-hub-${hub.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isCompleted ? 'bg-green-100' : 'bg-muted'}`}>
                          <Icon className={`h-4 w-4 ${isCompleted ? 'text-green-600' : 'text-muted-foreground'}`} strokeWidth={1.5} />
                        </div>
                        <span className="font-medium">{hub.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isCompleted ? (
                          <>
                            <Badge variant="secondary" className="bg-green-100 text-green-700">
                              {score?.toFixed(0)}%
                            </Badge>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </>
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                        )}
                      </div>
                    </Link>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recommended Next Steps */}
        <div className="animate-slide-up delay-300">
          <h2 className="font-serif text-xl font-semibold mb-4">Recommended Next Steps</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {hubsInfo
              .filter(hub => !progress?.completed_quizzes?.includes(hub.id))
              .slice(0, 3)
              .map((hub) => {
                const Icon = hub.icon;
                return (
                  <Link key={hub.id} to={hub.path}>
                    <Card className="hub-card h-full cursor-pointer">
                      <CardHeader>
                        <CardTitle className="font-serif text-lg flex items-center gap-2">
                          <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                          {hub.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Complete the quiz to mark progress
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            {progress?.completed_quizzes?.length === totalHubs && (
              <Card className="col-span-full text-center p-8">
                <Trophy className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
                <p className="font-serif text-xl font-semibold">Congratulations!</p>
                <p className="text-muted-foreground">
                  You've completed all hub quizzes. Consider retaking them or exploring the resources hub.
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
